<template>
  <div class="rich-editor-images">
    <b-form-group :label="label">
      <vue-editor
        class="rich-editor-images__editor"
        :value="value"
        :editor-toolbar="editorToolbar"
        :disabled="disabled"
        @input="$emit('input', $event)"
        @ready="onEditorReady"
      ></vue-editor>
    </b-form-group>

    <slot name="after-editor"></slot>

    <div class="app-accordion-item rich-editor-images__gallery" v-if="withGallery">
      <button type="button" class="app-accordion-header" v-b-toggle="galleryAccordionId">
        <span class="app-accordion-header__icon"><AppIcon name="image" /></span>
        <span class="app-accordion-header__title">{{ imagesLabel }}</span>
        <AppIcon name="chevron-down" class="app-accordion-header__chevron" />
      </button>
      <b-collapse :id="galleryAccordionId">
        <div class="app-accordion-body">
          <Imagen
            :images="images"
            @load-image="onLoadImage"
            :typeImage="typeImage"
            :multipleImage="multipleImage"
            :maxImage="maxImage"
            :compressImage="compressImage"
          />
          <div class="rich-editor-images__thumbnails" v-if="galleryImages.length">
            <b-button
              v-for="path in galleryImages"
              :key="'rich-editor-image-' + path"
              variant="link"
              class="rich-editor-images__thumbnail-btn"
              @click="insertImage(path)"
            >
              <img :src="path" alt="Imagen" />
              <span>Insertar en detalle</span>
            </b-button>
          </div>
        </div>
      </b-collapse>
    </div>
  </div>
</template>

<script>
import { VueEditor } from 'vue2-editor'
import Imagen from '@/components/inc/image/image'

// Editor de texto enriquecido (Quill), opcionalmente con galería de
// imágenes (Imagen, inc/image) integrada. La galería y el contenido son
// independientes: una imagen pegada/insertada en el editor se guarda y se
// referencia en el HTML, mientras que la galería conserva únicamente los
// adjuntos que se cargan desde este acordeón.
//
// Las miniaturas permiten insertar en el detalle un adjunto ya cargado,
// sin duplicar el archivo ni convertirlo en una imagen propia del editor.
//
// La galería vive siempre dentro de su propio acordeón colapsado por
// default (mismo patrón app-accordion-item que el resto del sistema,
// ver docs/modal-interfaces/README.md sección 12) -- no se muestra hasta
// que el usuario la abre a propósito. El editor en sí nunca depende de
// esto: pegar una imagen directo en el texto sigue subiendo y linkeando
// sola, esté la galería abierta o no.
//
// Si el módulo consumidor no tiene un campo `images` propio (no maneja
// una galería separada del texto), usar :with-gallery="false" -- el
// editor sigue funcionando igual (pegar una imagen la sube y la vincula
// vía ProcessEditorImages() en el backend), simplemente no se muestra el
// acordeón de galería ni hace falta pasarle `images`/`type-image`.
export default {
  name: 'RichEditorImages',
  components: {
    VueEditor,
    Imagen,
  },
  props: {
    // v-model del HTML de la descripción.
    value: {
      type: String,
      default: '',
    },
    // Si el consumidor no tiene un campo de galería propio, usar
    // :with-gallery="false" -- solo se muestra el editor, `images` y
    // `type-image` dejan de ser necesarios.
    withGallery: {
      type: Boolean,
      default: true,
    },
    // Array de imágenes de la galería -- mismo contrato que el prop
    // `images` de Imagen (inc/image). Requerido solo si withGallery.
    images: {
      type: Array,
      default: () => [],
    },
    // Carpeta/tipo para Imagen (ej. 'Tasks'). Ver prop typeImage de
    // Imagen -- se usa para separar los uploads por módulo. Requerido
    // solo si withGallery.
    typeImage: {
      type: String,
      default: '',
    },
    maxImage: {
      type: Number,
      default: 5,
    },
    multipleImage: {
      type: Boolean,
      default: true,
    },
    compressImage: {
      type: Boolean,
      default: true,
    },
    label: {
      type: String,
      default: 'Detalle',
    },
    imagesLabel: {
      type: String,
      default: 'Imágenes',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    // Se conserva la botonera estándar de vue2-editor y se suma el
    // separador. Quill ya reconoce el blot `hr` (también al pegar HTML),
    // por lo que el contenido se guarda como un <hr> normal.
    editorToolbar() {
      return [
        [{ header: [false, 1, 2, 3, 4, 5, 6] }],
        ['bold', 'italic', 'underline', 'strike'],
        [{ align: '' }, { align: 'center' }, { align: 'right' }, { align: 'justify' }],
        ['blockquote', 'code-block'],
        [{ list: 'ordered' }, { list: 'bullet' }, { list: 'check' }],
        [{ indent: '-1' }, { indent: '+1' }],
        [{ color: [] }, { background: [] }],
        ['link', 'image', 'video'],
        ['hr'],
        ['clean'],
      ]
    },
    galleryImages() {
      return this.images.filter(image => image && image.path).map(image => image.path)
    },
    // Id único por instancia -- puede haber más de un RichEditorImages en
    // la misma página, y b-collapse necesita un id de DOM propio.
    galleryAccordionId() {
      return 'rich-editor-images-gallery-' + this._uid
    },
  },
  methods: {
    onEditorReady(quill) {
      var toolbar = quill.getModule('toolbar')
      if (!toolbar) return

      toolbar.addHandler('hr', () => {
        var range = quill.getSelection(true)
        if (!range) return

        quill.insertEmbed(range.index, 'hr', true, 'user')
        quill.insertText(range.index + 1, '\n', 'user')
        quill.setSelection(range.index + 2, 0, 'silent')
      })
    },
    // Normaliza lo que emite Imagen (puede traer strings sueltas o
    // objetos sin `path`) y respeta maxImage -- así el consumidor no
    // tiene que repetir esta lógica en cada módulo que use el componente.
    onLoadImage(images) {
      var normalized = (images || []).map(image => {
        if (!image) return null
        return typeof image === 'string' ? { path: image } : image
      }).filter(image => image && image.path).slice(0, this.maxImage)

      this.$emit('load-image', normalized)
    },
    insertImage(path) {
      if (!path) return
      this.$emit('input', (this.value || '') + '<p><img src="' + path + '" style="max-width:100%;"></p>')
    },
  },
}
</script>

<style>
.rich-editor-images__editor .ql-editor {
  min-height: 180px;
}
.rich-editor-images__editor .ql-toolbar .ql-formats .ql-hr::before {
  content: '\2015';
  display: block;
  font-size: 19px;
  font-weight: 700;
  line-height: 18px;
}
.rich-editor-images__editor .ql-toolbar .ql-formats .ql-hr {
  width: 28px;
}
.rich-editor-images__gallery {
  margin-top: 14px;
}
.app-accordion-item {
  border: 1px solid var(--app-color-border);
  border-radius: 8px;
  overflow: hidden;
  background: var(--app-color-surface);
}
.app-accordion-header {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 10px 14px;
  background: var(--app-color-surface-subtle);
  border: none;
  cursor: pointer;
  font-weight: 700;
  font-size: 14px;
  color: var(--app-color-text);
  text-align: left;
}
.app-accordion-header__icon {
  display: inline-flex;
  color: var(--app-color-primary);
}
.app-accordion-header__title {
  flex: 1 1 auto;
}
.app-accordion-header__chevron {
  transition: transform 0.15s ease;
  color: var(--app-color-text-muted);
}
.app-accordion-header.collapsed .app-accordion-header__chevron {
  transform: rotate(-90deg);
}
.app-accordion-body {
  padding: 14px;
  border-top: 1px solid var(--app-color-border);
}
.rich-editor-images__thumbnails {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 8px;
}
.rich-editor-images__thumbnail-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px;
  border: 1px solid var(--app-color-border);
  border-radius: 4px;
  color: var(--app-color-primary);
  font-size: 11px;
}
.rich-editor-images__thumbnail-btn img {
  width: 34px;
  height: 34px;
  border-radius: 4px;
  object-fit: cover;
}
</style>
